/**
 * Load-aware persist for optimistic collab (chat / Q&A / announcements).
 * LiveKit fan-out is already done; this retries Mongo only on transient failures.
 */

type PersistOpts = {
  attempts?: number;
  /** Base delay before first retry (ms). Doubles each attempt + jitter. */
  delayMs?: number;
  /** Abort a single attempt after this many ms (default 12s). */
  timeoutMs?: number;
};

const MAX_IN_FLIGHT = 4;
let inFlight = 0;
const waitQueue: Array<() => void> = [];

function acquireSlot(): Promise<void> {
  if (inFlight < MAX_IN_FLIGHT) {
    inFlight += 1;
    return Promise.resolve();
  }
  return new Promise((resolve) => {
    waitQueue.push(() => {
      inFlight += 1;
      resolve();
    });
  });
}

function releaseSlot(): void {
  inFlight = Math.max(0, inFlight - 1);
  const next = waitQueue.shift();
  if (next) next();
}

function isRetryable(err: unknown): boolean {
  if (err == null) return false;
  const anyErr = err as {
    status?: number;
    statusCode?: number;
    response?: { status?: number };
    code?: string;
    name?: string;
    message?: string;
  };
  const status =
    anyErr.status ?? anyErr.statusCode ?? anyErr.response?.status ?? undefined;
  if (typeof status === "number") {
    if (status === 408 || status === 429) return true;
    if (status >= 500 && status < 600) return true;
    return false; // 4xx (validation / auth) — do not retry
  }
  const code = String(anyErr.code || "").toUpperCase();
  if (
    code === "ECONNABORTED" ||
    code === "ERR_NETWORK" ||
    code === "ETIMEDOUT" ||
    code === "ECONNRESET"
  ) {
    return true;
  }
  if (anyErr.name === "AbortError" || anyErr.name === "TimeoutError") return true;
  const msg = String(anyErr.message || "").toLowerCase();
  if (
    msg.includes("network") ||
    msg.includes("timeout") ||
    msg.includes("failed to fetch") ||
    msg.includes("load failed")
  ) {
    return true;
  }
  // Unknown / axios without status — treat as transient under load
  return true;
}

function backoffMs(base: number, attemptIndex: number): number {
  const exp = base * 2 ** attemptIndex;
  const jitter = Math.floor(Math.random() * Math.min(250, exp * 0.35));
  return Math.min(8000, exp + jitter);
}

async function withTimeout<T>(fn: () => Promise<T>, timeoutMs: number): Promise<T> {
  if (!timeoutMs || timeoutMs <= 0) return fn();
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      fn(),
      new Promise<T>((_, reject) => {
        timer = setTimeout(() => {
          const e = new Error("persist timeout");
          (e as { name?: string; code?: string }).name = "TimeoutError";
          (e as { code?: string }).code = "ETIMEDOUT";
          reject(e);
        }, timeoutMs);
      }),
    ]);
  } finally {
    if (timer) clearTimeout(timer);
  }
}

export async function persistWithRetry<T>(
  fn: () => Promise<T>,
  opts?: PersistOpts,
): Promise<T> {
  const attempts = Math.max(1, opts?.attempts ?? 3);
  const delayMs = Math.max(0, opts?.delayMs ?? 400);
  const timeoutMs = opts?.timeoutMs ?? 12_000;
  let lastErr: unknown;

  await acquireSlot();
  try {
    for (let i = 0; i < attempts; i += 1) {
      try {
        return await withTimeout(fn, timeoutMs);
      } catch (err) {
        lastErr = err;
        const canRetry = i < attempts - 1 && isRetryable(err);
        if (!canRetry) break;
        if (delayMs > 0) {
          await new Promise((r) => setTimeout(r, backoffMs(delayMs, i)));
        }
      }
    }
  } finally {
    releaseSlot();
  }
  throw lastErr;
}
